/**
 * Utilitário para gerenciar cores e garantir contraste adequado
 * Implementa o algoritmo de luminância relativa (WCAG)
 */

/**
 * Converte uma cor hexadecimal para RGB
 * @param {string} hex - Cor em formato hexadecimal (ex: #ffffff)
 * @returns {object} Objeto com propriedades r, g, b
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 };
};

/**
 * Converte RGB para hexadecimal
 * @param {number} r - Valor de vermelho (0-255)
 * @param {number} g - Valor de verde (0-255)
 * @param {number} b - Valor de azul (0-255)
 * @returns {string} Cor em formato hexadecimal
 */
export const rgbToHex = (r, g, b) => {
  return "#" + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }).join("");
};

/**
 * Calcula a luminância relativa de uma cor (WCAG)
 * @param {string} hex - Cor em formato hexadecimal
 * @returns {number} Valor de luminância entre 0 e 1
 */
export const getLuminance = (hex) => {
  const rgb = hexToRgb(hex);
  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(val => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Calcula o contraste entre duas cores (WCAG)
 * @param {string} color1 - Primeira cor em hexadecimal
 * @param {string} color2 - Segunda cor em hexadecimal
 * @returns {number} Razão de contraste entre 1 e 21
 */
export const getContrastRatio = (color1, color2) => {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Determina se o contraste é adequado
 * @param {string} color1 - Primeira cor
 * @param {string} color2 - Segunda cor
 * @param {string} level - Nível de conformidade: 'AA' (4.5:1) ou 'AAA' (7:1)
 * @returns {boolean} True se o contraste é adequado
 */
export const isContrastAdequate = (color1, color2, level = 'AA') => {
  const ratio = getContrastRatio(color1, color2);
  return level === 'AAA' ? ratio >= 7 : ratio >= 4.5;
};

/**
 * Escolhe a melhor cor de texto (branco ou preto) baseado no fundo
 * @param {string} backgroundColor - Cor de fundo em hexadecimal
 * @returns {string} '#ffffff' para texto branco ou '#000000' para texto preto
 */
export const getTextColor = (backgroundColor) => {
  const luminance = getLuminance(backgroundColor);
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

/**
 * Encontra uma cor de texto com contraste adequado
 * Tenta branco e preto primeiro, depois ajusta se necessário
 * @param {string} backgroundColor - Cor de fundo
 * @param {string} preferredColor - Cor preferida (opcional)
 * @returns {string} Cor com contraste adequado
 */
export const getContrastingTextColor = (backgroundColor, preferredColor = null) => {
  // Se uma cor preferida foi fornecida, verifica se tem contraste adequado
  if (preferredColor) {
    if (isContrastAdequate(backgroundColor, preferredColor, 'AA')) {
      return preferredColor;
    }
  }

  // Tenta branco primeiro
  const white = '#ffffff';
  if (isContrastAdequate(backgroundColor, white, 'AA')) {
    return white;
  }

  // Se branco não funciona, usa preto
  const black = '#000000';
  if (isContrastAdequate(backgroundColor, black, 'AA')) {
    return black;
  }

  // Fallback: retorna a cor que tem melhor contraste
  const whiteContrast = getContrastRatio(backgroundColor, white);
  const blackContrast = getContrastRatio(backgroundColor, black);
  return whiteContrast > blackContrast ? white : black;
};

/**
 * Ajusta o brilho de uma cor
 * @param {string} hex - Cor em hexadecimal
 * @param {number} percent - Percentual de ajuste (-100 a 100)
 * @returns {string} Cor ajustada em hexadecimal
 */
export const adjustBrightness = (hex, percent) => {
  const rgb = hexToRgb(hex);
  const factor = 1 + (percent / 100);
  
  const r = Math.min(255, Math.max(0, Math.round(rgb.r * factor)));
  const g = Math.min(255, Math.max(0, Math.round(rgb.g * factor)));
  const b = Math.min(255, Math.max(0, Math.round(rgb.b * factor)));
  
  return rgbToHex(r, g, b);
};

/**
 * Gera uma paleta de cores com contraste garantido
 * @param {object} colors - Objeto com cores personalizadas
 * @returns {object} Paleta com cores ajustadas para contraste
 */
export const generateContrastPalette = (colors) => {
  const palette = { ...colors };

  // Garante texto com contraste no fundo primário
  if (palette.primaryColor && !palette.textColor) {
    palette.textColor = getContrastingTextColor(palette.primaryColor);
  }

  // Garante texto com contraste no botão
  if (palette.buttonColor && !palette.buttonTextColor) {
    palette.buttonTextColor = getContrastingTextColor(palette.buttonColor);
  }

  // Garante texto com contraste no botão secundário
  if (palette.secondaryColor && !palette.secondaryTextColor) {
    palette.secondaryTextColor = getContrastingTextColor(palette.secondaryColor);
  }

  return palette;
};

/**
 * Valida uma paleta de cores e retorna relatório de contraste
 * @param {object} colors - Paleta de cores
 * @returns {object} Relatório com informações de contraste
 */
export const validateColorPalette = (colors) => {
  const report = {
    valid: true,
    issues: [],
    ratios: {}
  };

  // Verifica contraste entre fundo primário e texto
  if (colors.primaryColor && colors.textColor) {
    const ratio = getContrastRatio(colors.primaryColor, colors.textColor);
    report.ratios.primaryText = ratio;
    if (!isContrastAdequate(colors.primaryColor, colors.textColor, 'AA')) {
      report.valid = false;
      report.issues.push(
        `Contraste inadequado entre primaryColor e textColor (${ratio.toFixed(2)}:1). Mínimo recomendado: 4.5:1`
      );
    }
  }

  // Verifica contraste entre botão e texto do botão
  if (colors.buttonColor && colors.buttonTextColor) {
    const ratio = getContrastRatio(colors.buttonColor, colors.buttonTextColor);
    report.ratios.buttonText = ratio;
    if (!isContrastAdequate(colors.buttonColor, colors.buttonTextColor, 'AA')) {
      report.valid = false;
      report.issues.push(
        `Contraste inadequado entre buttonColor e buttonTextColor (${ratio.toFixed(2)}:1). Mínimo recomendado: 4.5:1`
      );
    }
  }

  return report;
};

export default {
  hexToRgb,
  rgbToHex,
  getLuminance,
  getContrastRatio,
  isContrastAdequate,
  getTextColor,
  getContrastingTextColor,
  adjustBrightness,
  generateContrastPalette,
  validateColorPalette
};

